import { useState, useEffect, useRef } from 'react';
import { X, Building2, Check } from 'lucide-react';
import api from '../../services/api';
import { organizationsAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function RolloutCourseModal({ course, organizationId, onClose }) {
  const [facilities, setFacilities] = useState([]);
  const [rolledOutFacilityIds, setRolledOutFacilityIds] = useState(new Set());
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [commercialCoverage, setCommercialCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const triggerRef = useRef(document.activeElement);

  useEffect(() => {
    const trigger = triggerRef.current;
    return () => { trigger?.focus(); };
  }, []);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [facilitiesRes, rolloutsRes, organizationRes] = await Promise.all([
        organizationsAPI.getMyFacilities(),
        api.get(`/lms/rollout/course/${course._id}/facilities`),
        organizationsAPI.getById(organizationId),
      ]);
      const orgFacilities = facilitiesRes.data?.facilities || facilitiesRes.data || [];
      setFacilities(orgFacilities);
      setCommercialCoverage(organizationRes.data?.organization?.serviceEntitlements?.lms?.coverage || null);

      const alreadyRolled = new Set(
        (rolloutsRes.data?.facilities || []).map((f) => String(f._id)),
      );
      setRolledOutFacilityIds(alreadyRolled);
      setSelectedFacilityIds(new Set(alreadyRolled));
    } catch (err) {
      console.error('Fetch rollout data error:', err);
      toast.error('Failed to load facility data');
    } finally {
      setLoading(false);
    }
  };

  const toggleFacility = (facilityId) => {
    const id = String(facilityId);
    setSelectedFacilityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const eligibleFacilityIds = facilities
      .filter((facility) => !isFacilityDisabled(facility))
      .map((facility) => String(facility._id));
    if (selectedFacilityIds.size === eligibleFacilityIds.length) {
      setSelectedFacilityIds(new Set());
    } else {
      setSelectedFacilityIds(new Set(eligibleFacilityIds));
    }
  };

  const handleSave = async () => {
    const newRollouts = [...selectedFacilityIds].filter((id) => !rolledOutFacilityIds.has(id));
    const revocations = [...rolledOutFacilityIds].filter((id) => !selectedFacilityIds.has(id));

    if (newRollouts.length === 0 && revocations.length === 0) {
      toast('No changes to save');
      return;
    }

    setSaving(true);
    try {
      if (newRollouts.length > 0) {
        const res = await api.post('/lms/rollout', {
          courseId: course._id,
          facilityIds: newRollouts,
        });
        const created = res.data?.created?.length || 0;
        if (created > 0) {
          toast.success(`Rolled out to ${created} facility(ies)`);
        }
        if (res.data?.errors?.length > 0) {
          for (const err of res.data.errors) {
            toast.error(err.error);
          }
        }
      }

      // Revocations need individual DELETE calls since the API works by rollout ID
      // For now we'll fetch rollout list and revoke matching ones
      if (revocations.length > 0) {
        const rolloutsRes = await api.get(
          `/lms/rollout/org/${organizationId}?courseId=${course._id}`,
        );
        const rolloutList = rolloutsRes.data?.rollouts || [];
        let revokedCount = 0;
        for (const facilityId of revocations) {
          const rollout = rolloutList.find(
            (r) => String(r.facilityId?._id || r.facilityId) === facilityId,
          );
          if (rollout) {
            await api.delete(`/lms/rollout/${rollout._id}`);
            revokedCount++;
          }
        }
        if (revokedCount > 0) {
          toast.success(`Revoked rollout from ${revokedCount} facility(ies)`);
        }
      }

      onClose();
    } catch (err) {
      console.error('Rollout save error:', err);
      toast.error(err?.response?.data?.error || 'Failed to update rollout');
    } finally {
      setSaving(false);
    }
  };

  const newCount = [...selectedFacilityIds].filter((id) => !rolledOutFacilityIds.has(id)).length;
  const revokeCount = [...rolledOutFacilityIds].filter((id) => !selectedFacilityIds.has(id)).length;
  const coverageScopeLabel = commercialCoverage?.scopeType === 'org_wide' ? 'Org-wide' : 'Facility-scoped';
  const coverageLimit = Number(
    commercialCoverage?.coveredFacilityCount || commercialCoverage?.includedFacilityCount || 0,
  );
  const coverageTotal = Number(commercialCoverage?.totalFacilityCount || facilities.length || 0);
  const coveredFacilityNames = Array.isArray(commercialCoverage?.facilityNames)
    ? commercialCoverage.facilityNames.filter(Boolean)
    : [];
  const exceedsCommercialCoverage =
    commercialCoverage?.scopeType !== 'org_wide' &&
    coverageLimit > 0 &&
    selectedFacilityIds.size > coverageLimit;
  const namedCoverageSet = new Set(coveredFacilityNames.map((name) => String(name).trim().toLowerCase()));
  const isFacilityOutsideNamedCoverage = (facility) => (
    commercialCoverage?.scopeType !== 'org_wide' &&
    namedCoverageSet.size > 0 &&
    !namedCoverageSet.has(String(facility?.name || '').trim().toLowerCase())
  );
  const isFacilityDisabled = (facility) => {
    const facilityId = String(facility?._id || '');
    if (selectedFacilityIds.has(facilityId)) return false;
    if (isFacilityOutsideNamedCoverage(facility)) return true;
    if (
      commercialCoverage?.scopeType !== 'org_wide' &&
      coverageLimit > 0 &&
      selectedFacilityIds.size >= coverageLimit
    ) {
      return true;
    }
    return false;
  };
  const hasNamedCoverageConflict = facilities.some((facility) => (
    selectedFacilityIds.has(String(facility._id)) && isFacilityOutsideNamedCoverage(facility)
  ));
  const saveBlocked = saving || (newCount === 0 && revokeCount === 0) || exceedsCommercialCoverage || hasNamedCoverageConflict;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Roll out ${course.title} to facilities`}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Roll Out to Facilities</h2>
            <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{course.title}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-600" />
            </div>
          ) : facilities.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No facilities found for your organization.</p>
          ) : (
            <>
              {commercialCoverage ? (
                <div className="mb-4 rounded-lg border border-primary-100 bg-primary-50 px-3 py-3 text-sm text-primary-900">
                  <div className="font-medium">Purchased LMS Coverage</div>
                  <div className="mt-1">
                    {coverageScopeLabel} · {coverageLimit || 1} of {coverageTotal || coverageLimit || 1} facilities covered
                  </div>
                  {coveredFacilityNames.length ? (
                    <div className="mt-1 text-xs text-primary-700">Covered facilities: {coveredFacilityNames.join(', ')}</div>
                  ) : null}
                  <div className="mt-1 text-xs text-primary-700">
                    Course rollout is operational. Purchased coverage still determines which facilities are commercially included.
                  </div>
                </div>
              ) : null}
              {exceedsCommercialCoverage ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  You have selected more facilities than the current LMS subscription covers. Deselect extra facilities or expand the LMS subscription before saving.
                </div>
              ) : null}
              {hasNamedCoverageConflict ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  One or more selected facilities are outside the named commercial coverage for this LMS subscription.
                </div>
              ) : null}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">
                  {selectedFacilityIds.size} of {facilities.length} selected
                </span>
                <button
                  onClick={toggleAll}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {selectedFacilityIds.size === facilities.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-2">
                {facilities.map((facility) => {
                  const fId = String(facility._id);
                  const isSelected = selectedFacilityIds.has(fId);
                  const isExisting = rolledOutFacilityIds.has(fId);
                  const isDisabled = isFacilityDisabled(facility);
                  const outsideCoverage = isFacilityOutsideNamedCoverage(facility);
                  return (
                    <label
                      key={fId}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        isDisabled ? 'cursor-not-allowed opacity-60' :
                        isSelected
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFacility(fId)}
                        disabled={isDisabled}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <Building2 size={16} className="text-gray-400 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{facility.name}</span>
                        {outsideCoverage ? (
                          <div className="mt-1 text-xs text-amber-700">Outside purchased LMS coverage</div>
                        ) : null}
                      </div>
                      {isExisting && isSelected && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} /> Active
                        </span>
                      )}
                      {isExisting && !isSelected && (
                        <span className="text-xs text-red-500">Will revoke</span>
                      )}
                      {!isExisting && isSelected && (
                        <span className="text-xs text-primary-600">New</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {newCount > 0 && <span className="text-primary-600">{newCount} new</span>}
            {newCount > 0 && revokeCount > 0 && <span> · </span>}
            {revokeCount > 0 && <span className="text-red-500">{revokeCount} revoke</span>}
            {newCount === 0 && revokeCount === 0 && <span>No changes</span>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveBlocked}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500"
            >
              {saving ? 'Saving...' : 'Save Rollout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
